import { Dispatch, FC, SetStateAction, useState } from 'react'
import { Input, InputPhoneNumber, RequiredAsterisk } from 'src/components'
import { ErrorBag, useDebounceAnalyticInput } from 'src/hooks'
import { ETrackingEvent } from '../../enums'

interface IFormData {
  companyPhoneNo?: string
  linkedIn?: string
  personalPhoneNo?: string
}

interface IProps {
  isClaim?: boolean
  formData?: IFormData
  errors?: ErrorBag<IFormData>
  loading?: boolean
  onChange: Dispatch<SetStateAction<IFormData>>
  isRecruiter?: boolean
  screen?: string
  title?: string
  subTitle?: string
}

export const CampaignCompanyVerify: FC<IProps> = (props) => {
  return (
    <div className="fx-1 fx-column gap-8 p-8 bg-neutral-20 w-100-p">
      <div className="fx-column gap-3">
        <div className="semibold-28 txt-black-01">{props.isClaim ? 'Claim a Job' : 'Create a Listing'}</div>
        <div className="semibold-16 txt-grey-01">{props.isClaim ? 'Verify Company' : 'Verifications'}</div>
      </div>

      <div
        className="fx-column gap-10 p-8 bg-neutral-white round-4"
        style={{ maxWidth: 950 }}
      >
        <CampaignCompanyVerifyForm {...props}/>
      </div>
    </div>
  )
}

export const CampaignCompanyVerifyForm: FC<IProps> = ({
  screen = 'create_job',
  formData,
  loading,
  errors,
  onChange,
  isRecruiter,
  title = 'To verify your job listing, please provide your phone number.',
  subTitle = 'The verification process may take up to 3 business days. If verification is successful, your job listing will be published.'
}) => {
  const { analyticInput } = useDebounceAnalyticInput(screen)
  const [initialFormData] = useState(formData)
  return (
    <div className="fx-column gap-6">
      <div className="fx-column gap-1">
        <div className="bold-16 txt-black-01">{title}</div>
        <div className="lh-20 txt-black-02">{subTitle}</div>
      </div>

      <div className="fx-column gap-2">
        <div className="fw-700 txt-black-01 lh-20">
          Personal Phone Number<RequiredAsterisk/>
        </div>
        <InputPhoneNumber
          variant="secondary"
          name="personalPhoneNo"
          placeholder="818-288-2828"
          disabled={loading || !!initialFormData?.personalPhoneNo}
          readOnly={!!initialFormData?.personalPhoneNo}
          value={formData?.personalPhoneNo}
          error={errors?.getError('personalPhoneNo')}
          onChange={(e) => {
            const personalPhoneNo = e.target.value?.replace(/[^0-9]/g, '')
            analyticInput(ETrackingEvent.INPUT_PERSONAL_PHONE, personalPhoneNo)
            onChange((prev) => ({
              ...prev,
              personalPhoneNo
            }))
          }}
        />
      </div>

      <div className="fx-column gap-2">
        <div className="fw-700 txt-black-01 lh-20">
          Link to Your LinkedIn Profile<RequiredAsterisk/>
        </div>
        <Input
          className="py-1"
          name="linkedIn"
          placeholder="linkedin.com/in/"
          disabled={loading || !!initialFormData?.linkedIn}
          readOnly={!!initialFormData?.linkedIn}
          value={formData?.linkedIn}
          error={errors?.getError('linkedIn')}
          onChange={(e) => {
            analyticInput(ETrackingEvent.INPUT_LINKEDIN, e.target.value)
            onChange((prev) => ({
              ...prev,
              linkedIn: e.target.value
            }))
          }}
        />
      </div>

      <div className="fx-column gap-2">
        <div className="fw-700 txt-black-01 lh-20">
          {isRecruiter ? 'Agency Phone Number' : 'Company Phone Number'}
        </div>
        <InputPhoneNumber
          variant="secondary"
          name="companyPhoneNo"
          placeholder="818-288-2828"
          disabled={loading}
          value={formData?.companyPhoneNo}
          onChange={(e) => {
            const companyPhoneNo = e.target.value?.replace(/[^0-9]/g, '')
            analyticInput(ETrackingEvent.INPUT_AGENCY_PHONE, companyPhoneNo)
            onChange((prev) => ({
              ...prev,
              companyPhoneNo
            }))
          }}
        />

        <div className="lh-20 txt-grey-01 mt-1">
          Providing your Company Phone Number will help expedite the verification process.
        </div>
      </div>
    </div>
  )
}
